import { useEffect, useRef, useState } from 'react';
import ModalComponent from 'components/Common/Modal';
import ReportDesignButton, { hex_is_light } from 'components/Reports/ui/ReportDesignButton';
import exportAsImage from 'utils/exportAsImage';
import styles from 'components/Dashboard/Analytics/ui/DownloadChartModal/DownloadChart.module.scss';
import Logo from 'assets/img/logo.svg?react';
import Skeleton from 'react-loading-skeleton';
import { COUNT_MODE_OPTIONS, BREAKDOWN_OPTIONS } from 'components/Dashboard/Influence/CrmEngagementStats/breakdownDimensions';
import PlainBreakdownLegend from 'components/Dashboard/Influence/CrmEngagementStats/ui/PlainBreakdownLegend';

const getHtml2Canvas = async () => {
  const module = await import('html2canvas');
  return module.default;
};

// A slimmed-down copy of Analytics' own DownloadChartModal/DownloadChartPopUp - that component's chart
// keys come from indexing into a live Chart.js instance (chartRef.current.getDatasetMeta(index)) with
// assumptions about backgroundColor being an array, tuned for Mentions' own per-datapoint diagonal-
// pattern bars. Our chart's colours are simpler (a single colour per series, from seriesColours.js),
// so the keys here are built straight from that instead of introspecting Chart.js.
const EngagementStatsDownloadPopUp = ({ chartSection, chartKeys, localState, initialHeader, setIsOpen }) => {
  const [chartHeader, setChartHeader] = useState(initialHeader);
  const [image, setImage] = useState(null);
  const [showChartTitle, setShowChartTitle] = useState(true);
  const [designState, setDesignState] = useState({ backgroundColour: '#00122B', includeBranding: true });
  const [isOpenDesign, setIsOpenDesign] = useState(false);
  const exportRef = useRef(null);
  const mainContainer = useRef(null);
  const { width, left, top, height } = mainContainer?.current?.getBoundingClientRect() ?? {};

  const countModeLabel = COUNT_MODE_OPTIONS.find((option) => option.value === localState?.countMode)?.label ?? '';
  const breakdownLabel = BREAKDOWN_OPTIONS.find((option) => option.value === localState?.breakdownBy)?.label ?? '';
  const chartTitle = `${countModeLabel} ${breakdownLabel}`.trim();

  useEffect(() => {
    const timeout = setTimeout(async () => {
      const html2canvas = await getHtml2Canvas();
      const canvas = await html2canvas(chartSection.current);
      setImage(canvas.toDataURL('image/png', 1.0));
    }, 800);
    return () => clearTimeout(timeout);
  }, [chartSection]);

  const downloadChart = () => {
    exportAsImage(
      exportRef.current,
      String(chartHeader)
        .replace(/[^a-z0-9]/gi, ' ')
        .trim()
        .split(' ')
        .join('_')
        .toLowerCase()
        .replaceAll(/_+/gi, '_')
    );
  };

  return (
    <div ref={mainContainer}>
      <div className='flex-centered justify-content-between section-title py-3 px-5 website-popup-title'>
        <h3 className={styles.modalChartHeader}>Download this chart</h3>
        <ReportDesignButton
          property='enableAnalyticsRemoveBranding'
          customColours={[
            '#00122B',
            '#0094CC',
            '#F4EFED',
            '#F2A805',
            '#FBDBC5',
            '#FFE9B5',
            '#E2F1EB',
            '#D2E9E0',
            '#DFF1FB',
            '#FFFFFF',
          ]}
          designProps={{ designState, setDesignState }}
          componentText='chart'
          chartButton
          isOpenDesign={isOpenDesign}
          setIsOpenDesign={setIsOpenDesign}
        />
      </div>
      <div className={styles.modalChartContainer}>
        <div className={styles.headerTitleGroup}>
          <div className={styles.headerInputGroup}>
            <label htmlFor='chartHeader' className={styles.headerInputGroup__label}>
              Header
            </label>
            <input
              type='text'
              id='chartHeader'
              className={styles.headerInputGroup__input}
              placeholder='Type header...'
              value={chartHeader}
              onChange={(e) => setChartHeader(e.target.value)}
            />
          </div>

          <div className={styles.checkBoxesGroup}>
            <div className={styles.checkShowTitle}>
              <p>Show title</p>
              <div>
                <input
                  type='checkbox'
                  id='showTitle'
                  className={styles.checkShowTitle__input}
                  checked={showChartTitle}
                  onChange={() => setShowChartTitle(!showChartTitle)}
                />
                <label htmlFor='showTitle' className={styles.checkShowTitle__label} />
              </div>
            </div>
          </div>
        </div>
        <div
          className={styles.DownloadChart}
          ref={exportRef}
          style={{
            backgroundColor: designState?.backgroundColour,
            color: hex_is_light(designState?.backgroundColour) ? '#00122B' : '#F4EFED',
          }}
        >
          <h4 className={styles.DownloadChart__header}>{chartHeader}</h4>
          {image ? (
            <div className={styles.DownloadChart__imgContainer}>
              {showChartTitle && chartTitle && <p className={styles.DownloadChart__sectionTitle}>{chartTitle}</p>}
              <PlainBreakdownLegend items={chartKeys} />
              <img alt={`${chartHeader}-chart`} src={image} />
            </div>
          ) : (
            <div className={styles.skeletonImgContainer}>
              <Skeleton />
            </div>
          )}
          <div
            className={styles.DownloadChart__logoContainer}
            style={{ visibility: designState?.includeBranding ? 'visible' : 'hidden' }}
          >
            <p
              style={{ color: hex_is_light(designState?.backgroundColour) ? '#00122B' : '#F4EFED' }}
              className={styles.DownloadChart__helperText}
            >
              Powered by
            </p>
            <Logo fill={hex_is_light(designState?.backgroundColour) ? '#00122B' : '#F4EFED'} />
          </div>
        </div>
      </div>
      <div className={styles.DownloadChart__buttonGroup}>
        <button onClick={() => setIsOpen(false)} className={styles.DownloadChart__buttonGroup_button}>
          Cancel
        </button>
        <button onClick={downloadChart} className={styles.DownloadChart__buttonGroup_button_download}>
          Download
        </button>
      </div>
      {isOpenDesign && (
        <div
          className='position-fixed rounded z-extra-max'
          style={{
            width: `${width}px`,
            height: `${height}px`,
            top: `${top}px`,
            left: `${left}px`,
            background: 'rgba(0, 18, 43, 0.06)',
          }}
        />
      )}
    </div>
  );
};

const EngagementStatsDownloadModal = ({ isOpen, setIsOpen, chartSection, chartKeys, localState }) => (
  <ModalComponent
    isOpen={isOpen}
    setIsOpen={setIsOpen}
    additionalClass='create-download-chart-simple-modal'
    style={{ maxHeight: '84%', maxWidth: '790px', overflow: 'hidden auto' }}
  >
    <EngagementStatsDownloadPopUp
      chartSection={chartSection}
      chartKeys={chartKeys}
      localState={localState}
      initialHeader='Engagement stats'
      setIsOpen={setIsOpen}
    />
  </ModalComponent>
);

export default EngagementStatsDownloadModal;
